import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import NotificationDropdown from "@/components/NotificationDropdown";
import { getUnreadCount } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

// How often to poll for new notifications while a session is open. Not so frequent that it's
// wasteful, frequent enough that the bell badge doesn't feel stale.
const POLL_MS = 45_000;

function LogOutIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}

// Square bordered icon buttons (design system v1.1 `.icon-btn`: 38x38, radius 6px, faint fill).
// `appearance-none` strips the native OS button chrome — without it, WebKit/Chrome render a
// leftover native highlight/border around a plain <button> once the pointer leaves it after a
// hover (a well-known browser quirk, not something coming from our own border/outline styles).
// outline-none + a subtle on-brand focus-visible ring replaces the browser's default focus
// outline, which otherwise renders as a stark white box around the button after a click.
const ICON_BTN = "relative flex h-[38px] w-[38px] shrink-0 appearance-none items-center justify-center rounded-md outline-none transition-colors hover:border-primary/40 hover:text-text focus-visible:ring-2 focus-visible:ring-primary/40";
const ICON_BTN_STYLE = { background: "var(--glass)", border: "1px solid var(--border-light)", color: "var(--text-muted)" } as const;

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export default function AccountNav() {
  const { user, accessToken, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    if (!notifOpen) return;
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [notifOpen]);

  const refreshUnread = useCallback(() => {
    if (!accessToken) return;
    getUnreadCount(accessToken).then((r) => setUnreadCount(r.count)).catch(() => {});
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    refreshUnread();
    const id = setInterval(refreshUnread, POLL_MS);
    return () => clearInterval(id);
  }, [accessToken, refreshUnread]);

  // While a persisted session is still restoring, show a neutral placeholder instead of the
  // logged-out buttons — otherwise a refresh flashes "Log in / Sign up" before the avatar appears.
  if (!user && loading) {
    return <div className="h-[38px] w-[38px] shrink-0 animate-pulse rounded-full" style={{ background: "var(--glass)" }} />;
  }
  if (!user) {
    return (
      <div className="flex items-center" style={{ gap: 8 }}>
        <Link
          to="/login"
          className="transition-colors duration-150 hover:opacity-90"
          style={{
            display: "inline-flex", alignItems: "center",
            fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 500,
            padding: "9px 20px", borderRadius: 9999,
            border: "1px solid var(--border-light)",
            color: "var(--text-secondary)",
            whiteSpace: "nowrap",
          }}
        >
          Log in
        </Link>
        <Link
          to="/register"
          className="transition-opacity duration-150 hover:opacity-90"
          style={{
            display: "inline-flex", alignItems: "center",
            fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 500,
            padding: "9px 20px", borderRadius: 9999,
            background: "var(--brand)",
            color: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          Sign up
        </Link>
      </div>
    );
  }

  const initials = (user.display_name || user.email)
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <div className="flex items-center gap-2.5">
      {/* Publish skill — pill button, matching the login/sign-up pills. */}
      <Link
        to="/skills/new"
        title="Publish skill"
        className="inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-full px-5 font-display text-[13px] font-medium text-white transition-opacity hover:opacity-90"
        style={{ background: "var(--brand)", border: "1px solid var(--brand)" }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Publish
      </Link>

      {/* Notifications */}
      <div className="relative" ref={notifRef}>
        <button
          type="button"
          onClick={() => setNotifOpen((o) => !o)}
          title="Notifications"
          aria-label="Notifications"
          className={ICON_BTN}
          style={notifOpen ? { ...ICON_BTN_STYLE, borderColor: "rgba(225,100,46,.35)", color: "var(--brand)" } : ICON_BTN_STYLE}
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className="absolute right-[7px] top-[6px] h-[7px] w-[7px] rounded-full bg-primary" />
          )}
        </button>
        {notifOpen && accessToken && (
          <NotificationDropdown
            accessToken={accessToken}
            onClose={() => setNotifOpen(false)}
            onMarkedAllRead={() => setUnreadCount(0)}
            onMarkedOneRead={() => setUnreadCount((c) => Math.max(0, c - 1))}
          />
        )}
      </div>

      {/* Avatar button + dropdown */}
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex h-[38px] w-[38px] items-center justify-center rounded-full font-display text-sm font-medium transition-all duration-150 hover:scale-105"
          style={{ background: "rgba(225,100,46,.16)", border: "1px solid rgba(225,100,46,.3)", color: "var(--brand)" }}
          aria-label="Account menu"
        >
          {initials}
        </button>

        {open && (
          <div
            className="absolute right-0 top-full z-[200] mt-2 w-60 rounded-[16px]"
            style={{
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              boxShadow: "0 4px 6px rgba(0,0,0,0.05), 0 20px 40px rgba(0,0,0,0.12)",
              animation: "avatarMenuIn 0.15s cubic-bezier(0.22,1,0.36,1) both",
            }}
          >
            {/* User info card */}
            <div className="px-4 py-3.5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-display text-sm font-bold text-white">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold" style={{ color: "var(--text)" }}>
                    {user.display_name || user.email.split("@")[0]}
                  </p>
                  <p className="truncate text-xs" style={{ color: "var(--text-muted)" }}>
                    {user.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Admin link */}
            {user.roles?.some(r => r === "admin" || r.startsWith("admin:")) && (
              <div className="p-1.5" style={{ borderTop: "1px solid var(--border)" }}>
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors duration-150 hover:bg-white/5"
                  style={{ color: "var(--text-muted)" }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Admin Panel
                </Link>
              </div>
            )}

            {/* Profile — [profiles+follows] */}
            <div className="p-1.5" style={{ borderTop: "1px solid var(--border)" }}>
              <Link
                to={`/u/${user.username}`}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors duration-150 hover:bg-white/5"
                style={{ color: "var(--text-muted)" }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
                Profile
              </Link>
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors duration-150 hover:bg-white/5"
                style={{ color: "var(--text-muted)" }}
              >
                <SettingsIcon />
                Settings
              </Link>
            </div>

            {/* Log out */}
            <div className="p-1.5" style={{ borderTop: "1px solid var(--border)" }}>
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm font-medium text-red-400 transition-colors duration-150 hover:bg-red-500/10 hover:text-red-300"
              >
                <LogOutIcon />
                Log out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
